<script setup lang="ts">
import { useCourseStore } from '../stores/courseStore';
import type { Course, CourseCategory } from '../types/course';
import RoomModal from './RoomModal.vue';
import { ref, computed } from 'vue';
import { useSwipe } from '@vueuse/core';

const store = useCourseStore();
const selectedRoom = ref<string | null>(null);
const containerRef = ref<HTMLElement | null>(null);

const categories: CourseCategory[] = ['asanas', 'meditation', 'other offerings', 'my day'];

const handleCourseClick = (course: Course) => {
  store.toggleCourseSelection(course);
};

const handleRoomClick = (event: Event, room: string) => {
  // Only proceed if roomModalEnabled is true
  if (store.roomModalEnabled) {
    event.stopPropagation();
    selectedRoom.value = room;
  }
};

const currentIndex = computed(() => 
  categories.findIndex(cat => cat === store.selectedCategory)
);

const transform = computed(() => 
  `translateX(-${currentIndex.value * 100}vw)`
);

const dayMessage = computed(() => {
  switch (store.selectedDay) {
    case 'today':
      return 'heute';
    case 'tomorrow':
      return 'morgen';
    case 'day after tomorrow':
      return 'übermorgen';
    default:
      return '';
  }
});

useSwipe(containerRef, {
  onSwipeStart: (e) => {
    e.preventDefault();
  },
  onSwipe: (e) => {
    e.preventDefault();
  },
  onSwipeEnd: (e, direction) => {
    const idx = currentIndex.value;
    if (direction === 'left' && idx < categories.length - 1) {
      store.selectedCategory = categories[idx + 1];
    } else if (direction === 'right' && idx > 0) {
      store.selectedCategory = categories[idx - 1];
    }
  }
});
</script>

<template>
  <div class="courses-container" ref="containerRef">
    <div class="courses-wrapper" :style="{ transform }">
      <div v-for="category in categories" :key="category" class="category-section">
        <div class="course-list">
          <div
            v-if="category === 'my day' && store.getCoursesByCategory(category).length === 0"
            class="no-courses-message"
          >
            Du hast für {{ dayMessage }} noch keine Kurse ausgewählt
          </div>
          <div
            v-for="course in store.getCoursesByCategory(category)"
            :key="`${course.date}-${course.time}-${course.course_name}`"
            class="course-item"
            :class="{
              'course-selected': course.selected,
              'course-overlap': course.selected && store.isOverlapping(course)
            }"
            @click="handleCourseClick(course)"
          >
            <div class="course-time">{{ course.time }}</div>
            <div class="course-name">{{ course.course_name }}</div>
            <div class="course-select">
              <span class="star" :class="{ selected: course.selected }">★</span>
            </div>
            <div class="course-conductor">
              {{ course.conductor && store.showConductors ? course.conductor : "\u00A0" }}
            </div>
            <span 
              class="course-room"
              :class="{ 'clickable': store.roomModalEnabled }"
              @click="store.roomModalEnabled ? handleRoomClick($event, course.room) : undefined"
            >
              {{ course.room }}
            </span>
            <span v-if="course.rapla_id" class="course-rapla-id">
              {{ course.rapla_id }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <RoomModal
      v-if="selectedRoom"
      :room="selectedRoom"
      @close="selectedRoom = null"
    />
  </div>
</template>

<style scoped>
.courses-container {
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 48px;
  overflow: hidden;
  background: #f5f5f5;
}

.courses-wrapper {
  display: flex;
  height: 100%;
  transition: transform 0.3s ease;
}

.category-section {
  min-width: 100vw;
  height: 100%;
  overflow-y: auto;
  padding: 1rem;
  -webkit-overflow-scrolling: touch;
}

.course-list {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.no-courses-message {
  text-align: center;
  color: #666;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.course-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.25rem 1rem;
  align-items: start;
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.course-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.course-selected {
  background: #fff5f8;
  border: 1px solid #960040;
}

.course-overlap {
  background: #fee2e2;
  border: 2px solid #dc2626;
}

.course-time {
  color: #960040;
  font-weight: 500;
  white-space: nowrap;
}

.course-name {
  color: #333;
  font-weight: 500;
  line-height: 1.3;
  word-wrap: break-word;
}

.course-room {
  color: #666;
  font-size: 0.9rem;
}

.course-room.clickable {
  cursor: pointer;
}

.course-room.clickable:hover {
  color: #960040;
  text-decoration: underline;
}

.course-conductor {
  color: #999;
  font-size: 0.8rem;
  font-style: italic;
  min-height: 1.1em;
}

.course-rapla-id {
  display: block;
  color: #999;
  font-size: 0.7rem;
  font-family: monospace;
  margin-top: 0.15rem;
  word-break: break-all;
}

.course-select {
  display: flex;
  align-items: center;
  justify-content: center;
  grid-row: span 2;
}

.star {
  font-size: 1.5rem;
  color: #ddd;
  transition: color 0.2s ease;
}

.star.selected {
  color: #ffd21f;
}

@media (hover: hover) {
  .course-item:hover .star:not(.selected) {
    color: #eee;
  }
}
</style>